import React, { useState, useEffect } from 'react';
import { financeAPI } from '../../utils/api';
import { FaFileInvoiceDollar, FaRegCreditCard, FaCheckCircle, FaExclamationCircle } from 'react-icons/fa';
import DataTable from '../../components_shared/DataTable';
import LoadingSpinner from '../../components_shared/LoadingSpinner';
import './FinanceDashboardPage.css';
import { DashboardChartGrid, KpiGrid, PortalHero } from '../../components_shared/PortalDashboard';

export default function FinanceDashboardPage() {
  const [summary, setSummary] = useState(null);
  const [invoices, setInvoices] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    fetchSummary();
  }, []);

  const fetchSummary = async () => {
    try {
      setLoading(true);
      const [summaryRes, invoicesRes] = await Promise.all([
        financeAPI.summary(),
        financeAPI.invoices.getAll(),
      ]);
      setSummary(summaryRes.data);
      setInvoices((invoicesRes.data?.content || invoicesRes.data || []).slice(0, 5));
    } catch (e) {
      setError('Failed to load finance summary.');
    } finally {
      setLoading(false);
    }
  };

  if (loading) return <LoadingSpinner />;
  if (error) return <div className="eduss-error-alert">{error}</div>;

  const stats = [
    { label: 'Bills', value: summary?.bills?.total, desc: `${summary?.bills?.pending} Pending`, icon: <FaFileInvoiceDollar />, className: 'eduss-dashboard-card-blue' },
    { label: 'Invoices', value: summary?.invoices?.total, desc: `${summary?.invoices?.processed} Processed`, icon: <FaRegCreditCard />, className: 'eduss-dashboard-card-yellow' },
    { label: 'Vouchers', value: summary?.vouchers?.total, desc: `${summary?.vouchers?.paid} Paid`, icon: <FaCheckCircle />, className: 'eduss-dashboard-card-green' },
    { label: 'Expenses', value: summary?.expenses?.total, desc: `Rs. ${new Intl.NumberFormat().format(summary?.expenses?.amount || 0)}`, icon: <FaExclamationCircle />, className: 'eduss-dashboard-card-red' },
  ];

  const charts = [
    {
      title: 'Finance Documents',
      caption: 'Current cycle',
      type: 'bar',
      data: [
        { name: 'Invoices', value: Number(summary?.invoices?.total || 0) },
        { name: 'Bills', value: Number(summary?.bills?.total || 0) },
        { name: 'Vouchers', value: Number(summary?.vouchers?.total || 0) },
        { name: 'Expenses', value: Number(summary?.expenses?.total || 0) },
      ],
      color: '#0f6fad',
    },
    {
      title: 'Amount Movement',
      caption: 'Rupees',
      type: 'line',
      data: [
        { name: 'Invoice', value: Number(summary?.invoices?.amount || 0) },
        { name: 'Voucher', value: Number(summary?.vouchers?.amount || 0) },
        { name: 'Expense', value: Number(summary?.expenses?.amount || 0) },
      ],
      color: '#10ac84',
    },
  ];

  return (
    <div className="eduss-dashboard">
      <PortalHero title="Finance Dashboard" subtitle="Real-time tracking of invoices, bills, and treasury vouchers." />
      <KpiGrid cards={stats} />
      <DashboardChartGrid charts={charts} />

      <div className="eduss-dashboard-content" style={{ marginTop: '30px' }}>
        <h2>Finance Operations Console</h2>
        <div className="eduss-grid-2-col">
          <div className="eduss-card glassmorphism">
            <h3>Recent Invoices</h3>
            <DataTable 
              data={invoices}
              columns={[
                { header: 'ID', accessorKey: 'id' },
                { header: 'Amount', accessorKey: 'amount' },
                { header: 'Status', accessorKey: 'status' },
                { header: 'Date', accessorKey: 'createdAt' }
              ]}
              pageSize={5}
            />
          </div>
          <div className="eduss-card glassmorphism">
            <h3>Quick Actions</h3>
            <div className="eduss-action-grid">
              <button className="eduss-btn primary">Generate New Bill</button>
              <button className="eduss-btn secondary">Process Invoices</button>
              <button className="eduss-btn outline">View Vouchers</button>
              <button className="eduss-btn danger">Audit Logs</button>
            </div>
          </div>
        </div>
      </div>

    </div>
  );
}
